import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Settings as SettingsIcon, Save, Camera } from 'lucide-react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { db, storage, auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const Settings = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    businessName: '',
    phone: '',
    website: '',
    instagram: '',
    facebook: '',
    twitter: '',
    photoURL: '',
  });

  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setFormData({
            displayName: data.displayName || '',
            businessName: data.businessName || '',
            phone: data.phone || '',
            website: data.website || '',
            instagram: data.instagram || '',
            facebook: data.facebook || '',
            twitter: data.twitter || '',
            photoURL: data.photoURL || currentUser.photoURL || '',
          });
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUser) {
      alert('❌ You must be logged in to save settings');
      return;
    }

    setSaving(true);
    
    try {
      // Update Firestore (use setDoc with merge to create if doesn't exist)
      const userRef = doc(db, 'users', currentUser.uid);
      const dataToSave = {
        displayName: formData.displayName || '',
        businessName: formData.businessName || '',
        phone: formData.phone || '',
        website: formData.website || '',
        instagram: formData.instagram || '',
        facebook: formData.facebook || '',
        twitter: formData.twitter || '',
        email: currentUser.email,
        photoURL: formData.photoURL || '',
        updatedAt: new Date().toISOString(),
      };
      
      await setDoc(userRef, dataToSave, { merge: true });

      // Update auth profile if display name changed
      if (formData.displayName && formData.displayName !== currentUser.displayName) {
        await updateProfile(auth.currentUser, {
          displayName: formData.displayName
        });
      }

      setSaving(false);
      alert('✅ Settings saved successfully!');
      
      // Navigate to dashboard using React Router (NO window.location!)
      navigate('/dashboard', { replace: true });
      
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(`❌ Failed to save settings: ${error.message}`);
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    try {
      setUploadingPhoto(true);
      
      const fileName = `profile-photos/${currentUser.uid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await updateProfile(auth.currentUser, {
        photoURL: downloadURL
      });

      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        photoURL: downloadURL,
        email: currentUser.email,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      setFormData({ ...formData, photoURL: downloadURL });
      alert('✅ Profile photo updated successfully!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(`❌ Failed to upload photo: ${error.message}`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pastel-purple"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <SettingsIcon className="h-8 w-8 text-pastel-purple" />
          <div>
            <h1 className="text-3xl font-bold text-brand-brown">Settings</h1>
            <p className="text-brand-brown/60">Manage your profile and business information</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Profile Photo */}
          <div className="card p-6">
            <h2 className="text-xl font-bold text-brand-brown mb-4">Profile Photo</h2>
            <div className="flex items-center space-x-6">
              <div className="relative group">
                {formData.photoURL || currentUser.photoURL ? (
                  <img
                    src={formData.photoURL || currentUser.photoURL}
                    alt="Profile"
                    className="h-24 w-24 rounded-full object-cover ring-4 ring-pastel-purple"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-pastel-purple flex items-center justify-center ring-4 ring-pastel-purple">
                    <span className="text-white font-bold text-2xl">
                      {(formData.displayName || currentUser.email || 'U')[0].toUpperCase()}
                    </span>
                  </div>
                )}
                
                <label className="absolute inset-0 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  {uploadingPhoto ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  ) : (
                    <Camera className="h-8 w-8 text-white" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={uploadingPhoto}
                  />
                </label>
              </div>
              
              <div>
                <h3 className="font-medium text-brand-brown mb-1">Update your photo</h3>
                <p className="text-sm text-brand-brown/60 mb-3">
                  Click on the image to upload a new profile picture
                </p>
                <p className="text-xs text-brand-brown/50">
                  JPG, PNG or GIF (Max 5MB)
                </p>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="card p-6">
            <h2 className="text-xl font-bold text-brand-brown mb-4">Personal Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="Your name"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={currentUser?.email}
                  disabled
                  className="input bg-gray-50 cursor-not-allowed"
                />
                <p className="text-xs text-brand-brown/50 mt-1">
                  Email cannot be changed
                </p>
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="card p-6">
            <h2 className="text-xl font-bold text-brand-brown mb-4">Business Information</h2>
            <p className="text-sm text-brand-brown/60 mb-4">
              This information will appear in your Visit Summaries sent to clients
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Business Name
                </label>
                <input
                  type="text"
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  placeholder="e.g., Cozy Companion Pet Care"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://yourwebsite.com"
                  className="input"
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="card p-6">
            <h2 className="text-xl font-bold text-brand-brown mb-4">Social Media</h2>
            <p className="text-sm text-brand-brown/60 mb-4">
              Add your social media handles (optional)
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Instagram
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-brand-brown/60">@</span>
                  <input
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    placeholder="yourbusiness"
                    className="flex-1 input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Facebook
                </label>
                <input
                  type="text"
                  value={formData.facebook}
                  onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                  placeholder="facebook.com/yourbusiness"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-1">
                  Twitter / X
                </label>
                <div className="flex items-center space-x-2">
                  <span className="text-brand-brown/60">@</span>
                  <input
                    type="text"
                    value={formData.twitter}
                    onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                    placeholder="yourbusiness"
                    className="flex-1 input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary px-8 py-3 flex items-center space-x-2"
            >
              <Save className="h-5 w-5" />
              <span>{saving ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default Settings;
